import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTask } from '../context/TaskContext';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../context/ToastContext';
import TaskCard from '../components/TaskCard';
import TaskModal from '../components/TaskModal';
import KanbanView from '../components/KanbanView';
import Analytics from './Analytics';
import Profile from './Profile';

const STATUS_FILTERS = [
  { value: 'all',         label: 'Toutes',     dot: '#50507a' },
  { value: 'todo',        label: 'À faire',    dot: '#9090b8' },
  { value: 'in-progress', label: 'En cours',   dot: '#6c63ff' },
  { value: 'done',        label: 'Terminées',  dot: '#34d399' },
];
const PRIORITY_OPTS = [
  { value: 'all',    label: 'Toutes priorités' },
  { value: 'high',   label: '🔴 Haute' },
  { value: 'medium', label: '🟡 Moyenne' },
  { value: 'low',    label: '🟢 Faible' },
];
const SORT_OPTS = [
  { value: 'createdAt', label: 'Création' },
  { value: 'dueDate',   label: 'Échéance' },
  { value: 'priority',  label: 'Priorité' },
  { value: 'title',     label: 'Titre' },
];
const PAGES = ['tasks', 'kanban', 'analytics', 'profile'];
const PAGE_LABELS = { tasks: '📋 Mes tâches', kanban: '🗂 Kanban', analytics: '📊 Analytiques', profile: '👤 Profil' };
const PAGE_ICONS  = { tasks: '📋', kanban: '🗂', analytics: '📊', profile: '👤' };

export default function Dashboard() {
  const { user, logout }    = useAuth();
  const { tasks, stats, loading, filters, fetchTasks, createTask, updateTask, deleteTask, updateFilters } = useTask();
  const { isDark, toggleTheme } = useTheme();
  const toast = useToast();

  const [page,       setPage]       = useState('tasks');
  const [viewMode,   setViewMode]   = useState('grid');  // grid | list
  const [showModal,  setShowModal]  = useState(false);
  const [editTask,   setEditTask]   = useState(null);
  const [search,     setSearch]     = useState('');

  useEffect(() => { fetchTasks(); }, []);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => updateFilters({ search }), 380);
    return () => clearTimeout(t);
  }, [search]);

  const handleCreate = async data => {
    await createTask(data);
    toast.success('Tâche créée avec succès !');
  };
  const handleEdit = async data => {
    await updateTask(editTask._id, data);
    toast.success('Tâche mise à jour.');
  };
  const handleDelete = async id => {
    if (!window.confirm('Supprimer cette tâche ?')) return;
    await deleteTask(id);
    toast.success('Tâche supprimée.');
  };
  const handleToggle = async task => {
    const next = task.status === 'done' ? 'todo' : 'done';
    await updateTask(task._id, { status: next });
    toast.info(next === 'done' ? '✅ Tâche marquée terminée !' : '↩️ Tâche remise en cours.');
  };

  const openEdit = task => { setEditTask(task); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setEditTask(null); };

  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  const total = (stats.todo || 0) + (stats['in-progress'] || 0) + (stats.done || 0);
  const rate  = total ? Math.round(((stats.done || 0) / total) * 100) : 0;

  const overdue = tasks.filter(t => t.dueDate && t.status !== 'done' && new Date(t.dueDate) < new Date()).length;

  return (
    <div className="dashboard">
      {/* ── SIDEBAR ── */}
      <aside className="sidebar">
        <div className="sidebar-top">
          {/* Logo */}
          <div className="sidebar-logo">
            <div className="sidebar-logo-icon">⚡</div>
            <span className="sidebar-logo-name">TaskFlow</span>
          </div>

          {/* Navigation */}
          <div className="nav-section">
            <div className="nav-section-title">Navigation</div>
            {PAGES.map(p => (
              <div key={p} className={`nav-item ${page === p ? 'active' : ''}`} onClick={() => setPage(p)}>
                <span className="nav-item-icon">{PAGE_ICONS[p]}</span>
                {p === 'tasks'     ? 'Mes tâches'   :
                 p === 'kanban'    ? 'Vue Kanban'    :
                 p === 'analytics' ? 'Analytiques'   : 'Mon profil'}
                {p === 'tasks' && <span className="nav-item-badge">{total}</span>}
              </div>
            ))}
          </div>

          <div className="sidebar-divider" />

          {/* Status shortcuts */}
          <div className="nav-section">
            <div className="nav-section-title">Statuts</div>
            {STATUS_FILTERS.map(f => (
              <div key={f.value}
                className={`nav-item ${page === 'tasks' && filters.status === f.value ? 'active' : ''}`}
                onClick={() => { setPage('tasks'); updateFilters({ status: f.value }); }}
              >
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: f.dot, flexShrink: 0 }} />
                {f.label}
                <span className="nav-item-badge">
                  {f.value === 'all' ? total : (stats[f.value] || 0)}
                </span>
              </div>
            ))}
          </div>

          {overdue > 0 && (
            <>
              <div className="sidebar-divider" />
              <div className="nav-section">
                <div className="nav-item" onClick={() => { setPage('tasks'); updateFilters({ status: 'all' }); }}
                  style={{ color: 'var(--danger)', borderColor: 'rgba(248,113,113,0.2)', background: 'var(--danger-dim)' }}>
                  <span className="nav-item-icon">⏰</span>
                  En retard
                  <span className="nav-item-badge" style={{ color: 'var(--danger)', borderColor: 'rgba(248,113,113,0.25)' }}>{overdue}</span>
                </div>
              </div>
            </>
          )}

          <div className="sidebar-divider" />

          {/* Progress */}
          <div className="sidebar-progress">
            <div className="progress-label">
              <span className="progress-label-text">Complétion globale</span>
              <span className="progress-label-pct">{rate}%</span>
            </div>
            <div className="progress-track">
              <div className="progress-fill" style={{ width: `${rate}%` }} />
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="sidebar-bottom">
          <div className="user-card" onClick={() => setPage('profile')}>
            <div className="user-avatar">{initials}</div>
            <div className="user-info">
              <div className="user-name">{user?.name}</div>
              <div className="user-email">{user?.email}</div>
            </div>
          </div>
          <div className="sidebar-actions">
            <button className="theme-toggle" onClick={toggleTheme}>
              {isDark ? '☀️ Mode clair' : '🌙 Mode sombre'}
              <span className="theme-toggle-knob" />
            </button>
          </div>
          <div className="sidebar-actions" style={{ marginTop: '0.5rem' }}>
            <button className="btn btn-ghost btn-sm" onClick={logout} style={{ width: '100%', justifyContent: 'center' }}>
              ⎋ Déconnexion
            </button>
          </div>
        </div>
      </aside>

      {/* ── MAIN ── */}
      <main className="main-content">
        {/* Topbar */}
        <div className="topbar">
          <div className="topbar-title">{PAGE_LABELS[page]}</div>
          <div className="topbar-actions">
            {(page === 'tasks' || page === 'kanban') && (
              <div className="search-wrap">
                <span className="search-icon">🔍</span>
                <input type="text" placeholder="Rechercher…" value={search} onChange={e => setSearch(e.target.value)} />
              </div>
            )}
            {(page === 'tasks' || page === 'kanban') && (
              <button className="btn btn-primary btn-sm" onClick={() => { setEditTask(null); setShowModal(true); }}>
                ✚ Nouvelle tâche
              </button>
            )}
          </div>
        </div>

        <div className="page-content">
          {/* ── TASKS PAGE ── */}
          {page === 'tasks' && (
            <>
              {/* Filters */}
              <div className="filter-bar">
                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 700, whiteSpace: 'nowrap' }}>FILTRES</span>
                {STATUS_FILTERS.map(f => (
                  <button key={f.value} className={`filter-chip ${filters.status === f.value ? 'active' : ''}`}
                    onClick={() => updateFilters({ status: f.value })}>
                    <span className="filter-chip-dot" style={{ background: f.dot }} />
                    {f.label}
                  </button>
                ))}
                <span className="filter-sep" />
                <select className="filter-select" value={filters.priority} onChange={e => updateFilters({ priority: e.target.value })}>
                  {PRIORITY_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <select className="filter-select" value={filters.sortBy} onChange={e => updateFilters({ sortBy: e.target.value })}>
                  {SORT_OPTS.map(o => <option key={o.value} value={o.value}>Tri: {o.label}</option>)}
                </select>
                <select className="filter-select" value={filters.order} onChange={e => updateFilters({ order: e.target.value })}>
                  <option value="desc">↓ Desc</option>
                  <option value="asc">↑ Asc</option>
                </select>
                {(filters.status !== 'all' || filters.priority !== 'all' || search) && (
                  <button className="filter-chip" onClick={() => { setSearch(''); updateFilters({ status:'all', priority:'all', search:'' }); }}
                    style={{ color:'var(--danger)', borderColor:'rgba(248,113,113,0.25)' }}>
                    ✕ Réinitialiser
                  </button>
                )}
              </div>

              {/* Tasks area */}
              <div className="tasks-area">
                <div className="view-header">
                  <span className="view-title">{tasks.length} tâche{tasks.length !== 1 ? 's' : ''}</span>
                  <div className="view-switcher">
                    <button className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')} title="Grille">⊞</button>
                    <button className={`view-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="Liste">☰</button>
                  </div>
                </div>

                {loading ? (
                  <div className="loading-overlay"><div className="loader-ring" /></div>
                ) : tasks.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-icon">📭</div>
                    <h3>Aucune tâche trouvée</h3>
                    <p>{search || filters.status !== 'all' ? 'Modifiez vos filtres ou' : 'Cliquez sur'} <strong>✚ Nouvelle tâche</strong> pour commencer.</p>
                  </div>
                ) : (
                  <div className={viewMode === 'grid' ? 'tasks-grid' : 'tasks-list'}>
                    {tasks.map((task, i) => (
                      <div key={task._id} style={{ animationDelay: `${i * 0.04}s` }}>
                        <TaskCard task={task} onEdit={openEdit} onDelete={handleDelete} onToggle={handleToggle} listView={viewMode === 'list'} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── KANBAN PAGE ── */}
          {page === 'kanban' && (
            <div className="tasks-area">
              {loading ? (
                <div className="loading-overlay"><div className="loader-ring" /></div>
              ) : (
                <KanbanView tasks={tasks} onUpdate={updateTask} onEdit={openEdit} onDelete={handleDelete} />
              )}
            </div>
          )}

          {/* ── ANALYTICS PAGE ── */}
          {page === 'analytics' && <Analytics tasks={tasks} stats={stats} />}

          {/* ── PROFILE PAGE ── */}
          {page === 'profile' && <Profile />}
        </div>
      </main>

      {/* Modal */}
      {showModal && (
        <TaskModal task={editTask} onClose={closeModal}
          onSubmit={editTask ? handleEdit : handleCreate} />
      )}
    </div>
  );
}
